import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'What is a digital legacy message?', a: 'A digital legacy message is a written or multimedia expression — love, gratitude, wisdom, or farewell — preserved digitally so it endures beyond the moment. Unlike a text or email, it\'s designed to be a permanent keepsake.' },
  { q: 'How do I create a digital legacy?', a: 'Start by writing what matters most: messages to loved ones, life lessons you\'ve learned, memories you want preserved. Say It NOW helps you create beautiful digital messages that become lasting keepsakes.' },
  { q: 'Is a digital legacy message only for end-of-life?', a: 'Not at all. Digital legacy messages are for any moment you want to preserve — milestones, gratitude, life advice, family history, or simply telling someone they matter.' },
  { q: 'Can recipients save and revisit my message?', a: 'Yes. Every Living Announcement created through Say It NOW has a permanent, shareable link. Recipients can revisit it anytime, and it becomes part of the Living Tree — a visual map of your connections.' },
];

const related = [
  { label: 'Living Tribute Ideas', href: '/living-tribute-ideas' },
  { label: 'What to Say Before Someone Dies', href: '/what-to-say-before-someone-dies' },
  { label: 'Message to Someone You Love', href: '/message-to-someone-you-love' },
  { label: 'Thank You Message for Parents', href: '/thank-you-message-for-parents' },
];

export default function DigitalLegacyMessage() {
  return (
    <SEOPageLayout
      title="Digital Legacy Message — Preserve Your Words"
      metaDescription="Create a digital legacy message that lasts forever. Express love, gratitude, and wisdom through a beautiful digital keepsake. Free to create. Built to endure."
      h1="Digital Legacy Messages — Your Words, Preserved Forever"
      subtitle="Some words are too important to exist only in the moment. Make them permanent."
      faq={faq}
      relatedPages={related}
    >
      <h2>Why Your Words Need to Outlive the Moment</h2>
      <p>
        Think about the most meaningful thing anyone has ever said to you. Now imagine if those words had been written down — preserved in a way you could return to whenever you needed them. That's the power of a <strong>digital legacy message</strong>.
      </p>
      <p>
        We live in an age of ephemeral communication. Messages disappear, chats get buried, and the most important things we say are often lost to the noise of daily life. A digital legacy message is different. It's intentional. It's permanent. It's designed to endure.
      </p>

      <h2>What Makes a Digital Legacy Message Different</h2>
      <p>A digital legacy message isn't just a longer text or a heartfelt email. It's:</p>
      <ul>
        <li><strong>Beautifully presented:</strong> Wrapped in a design that matches the gravity of the words.</li>
        <li><strong>Permanently accessible:</strong> Available via a unique link that never expires.</li>
        <li><strong>Emotionally rich:</strong> Can include photos, voice recordings, and reveal animations.</li>
        <li><strong>Part of something larger:</strong> Connected to a visual legacy through the <Link to="/faq">Living Tree</Link>.</li>
      </ul>

      <h2>Types of Digital Legacy Messages</h2>

      <h3>Love Letters That Last</h3>
      <p>Write to your partner, your children, or your parents. Tell them what they mean to you in words that will still be there in 20 years.</p>

      <h3>Wisdom for Future Generations</h3>
      <p>Share the life lessons you've learned — the mistakes that taught you, the principles that guided you, the advice you wish someone had given you.</p>

      <h3>Gratitude Archives</h3>
      <p>Create a collection of thank-you messages for the people who shaped your life. Together, they form a mosaic of the relationships that defined you.</p>

      <h3>Milestone Markers</h3>
      <p>Capture what you're feeling at pivotal moments — a wedding, a birth, a graduation, a loss. Future you (and future them) will be grateful for the record.</p>

      <h2>How Say It NOW Makes Digital Legacy Easy</h2>
      <p>
        <Link to="/signup">Say It NOW</Link> was built around the belief that everyone has words worth preserving. Here's how it works:
      </p>
      <ol>
        <li><strong>Write your message</strong> — guided by AI if you need help finding the words.</li>
        <li><strong>Choose a beautiful template</strong> — premium designs that elevate your words.</li>
        <li><strong>Add photos and media</strong> — make it personal and vivid.</li>
        <li><strong>Share privately</strong> — your message gets a unique, permanent link.</li>
        <li><strong>Watch it grow</strong> — each message becomes a branch on your Living Tree.</li>
      </ol>

      <h2>Start Your Digital Legacy Today</h2>
      <p>
        You don't need to be a writer. You don't need to be dying. You just need to have something worth saying — and everyone does. <Link to="/signup">Create your first digital legacy message</Link> for free. Because the best time to preserve your words was yesterday. The next best time is <strong>now</strong>.
      </p>
    </SEOPageLayout>
  );
}
